/**
 * Placing streaming transcription text into a form field.
 *
 * `gpt-live-transcribe` streams deltas while the physician is still speaking and
 * then sends an authoritative final transcript when the utterance is committed,
 * so text we wrote a moment ago routinely has to be *replaced* rather than
 * appended to. These helpers track the span we last wrote and rewrite it in
 * place, while refusing to touch text the physician has edited.
 *
 * Deliberately keyed on the span itself, not on OpenAI's `item_id`: committing
 * an utterance can produce a final whose item differs from the deltas that
 * preceded it, and matching on the id then appends the corrected sentence
 * underneath the raw one instead of replacing it.
 */

/** The span of field text we last wrote for the open utterance. */
export interface TranscriptRegion {
  /** Index in the field value where our text begins. */
  start: number;
  /** Exactly the text we wrote, including any leading space we added. */
  text: string;
}

export interface TranscriptPlacement {
  value: string;
  region: TranscriptRegion;
  cursor: number;
}

/** Dictation runs into prose, so a new utterance needs separating from the last. */
export const needsLeadingSpace = (before: string): boolean =>
  before.length > 0 && !/\s$/.test(before);

/**
 * True when the field still holds exactly the text we wrote at `region.start`.
 *
 * An utterance stays open for seconds — long enough for the physician to click
 * elsewhere and type. Splicing a stale region would corrupt their edit, so
 * callers fall back to inserting at the cursor instead.
 */
export const isRegionIntact = (
  value: string,
  region: TranscriptRegion,
): boolean =>
  value.slice(region.start, region.start + region.text.length) === region.text;

/**
 * A run of text that already closed a sentence, so the next one legitimately
 * starts with a capital. Allows a trailing quote or bracket after the stop.
 */
const SENTENCE_CLOSED = /[.!?:][")'\]]?$/;

/**
 * A leading word safe to lower-case when it turns out to be mid-sentence:
 * capitalised with an all-lower-case tail. Excludes "I" and "I'm" (no
 * lower-case-only tail), and acronyms like ROM or MRI (no lower-case tail at
 * all), both of which keep their capital wherever they appear.
 */
const DECAPITALIZABLE = /^[A-Z][a-z]+$/;

/**
 * Lower-case a fragment's first word when it continues the previous sentence.
 *
 * Each committed utterance is transcribed on its own, so the model capitalises
 * every one as though it were a new sentence. Joined back together, continuous
 * speech comes out as "I Maybe Wow, telling you". Whether a capital is correct
 * depends on the text before it, which only we can see — so the repair belongs
 * here rather than in the prompt.
 *
 * Conservative by design: a fragment opening with an abbreviation ("Mr.") or an
 * acronym keeps its capital. The known false positive is a proper noun that
 * happens to start a fragment, which would be lower-cased.
 */
export const decapitalizeContinuation = (
  text: string,
  before: string,
): string => {
  const preceding = before.trimEnd();
  if (preceding.length === 0 || SENTENCE_CLOSED.test(preceding)) {
    return text;
  }

  const firstWord = /^\S+/.exec(text)?.[0];
  if (firstWord === undefined || firstWord.endsWith(".")) {
    // A trailing period means an abbreviation, or a one-word sentence.
    return text;
  }

  const bare = firstWord.replace(/[^A-Za-z']+$/, "");
  if (!DECAPITALIZABLE.test(bare)) {
    return text;
  }

  return text[0].toLowerCase() + text.slice(1);
};

/**
 * Write `text` over the span we last wrote for the open utterance, falling back
 * to inserting at `cursor` when no span is open or the physician has edited over
 * it. Callers clear `region` to close an utterance, so the next one starts a
 * fresh span after the committed text.
 */
export const placeTranscript = ({
  value,
  cursor,
  region,
  text,
  repairContinuationCase = false,
}: {
  value: string;
  cursor: number;
  region: TranscriptRegion | null;
  text: string;
  /**
   * Lower-case a leading capital that turns out to be mid-sentence. Only the
   * streaming surface needs it — see `decapitalizeContinuation`. Off by default
   * so a caller whose transcripts arrive whole keeps its capitals.
   */
  repairContinuationCase?: boolean;
}): TranscriptPlacement => {
  const replaceable = region !== null && isRegionIntact(value, region);

  const start = replaceable ? region.start : cursor;
  const end = replaceable ? region.start + region.text.length : cursor;

  const before = value.slice(0, start);
  const after = value.slice(end);
  // Streaming ASR deltas carry their own leading space (" hello"). The
  // separator is ours to decide from `before`, so the text's own leading
  // whitespace is dropped — keeping both is how utterance boundaries grew
  // double and triple spaces.
  const bare = text.replace(/^\s+/, "");
  const joined = repairContinuationCase
    ? decapitalizeContinuation(bare, before)
    : bare;
  const written = needsLeadingSpace(before) ? ` ${joined}` : joined;

  return {
    value: before + written + after,
    region: { start, text: written },
    cursor: start + written.length,
  };
};

/** The result of rewriting a span in place. */
export interface RegionReplacement {
  /** Field value with the span rewritten. */
  value: string;
  /** The span as it now stands, ready to be rewritten again. */
  region: TranscriptRegion;
  /**
   * Change in length at `region.start`. Anything recorded at a higher offset —
   * a later utterance's span, the caret — has moved by this much.
   */
  shift: number;
}

/**
 * Rewrite `region` with `text`, or return null if the field no longer holds it.
 *
 * Any leading whitespace in the span is ours (see `needsLeadingSpace`) and is
 * kept, so a rewrite can't run the utterance into the word before it.
 */
export const replaceRegion = (
  value: string,
  region: TranscriptRegion,
  text: string,
): RegionReplacement | null => {
  if (!isRegionIntact(value, region)) {
    return null;
  }

  // At most one space survives: the span's leading whitespace is our
  // separator, and preserving a longer run verbatim would carry any
  // accumulated double-spacing into every rewrite of the span.
  const leading = /^\s/.test(region.text) ? " " : "";
  const written = leading + text;
  const end = region.start + region.text.length;

  return {
    value: value.slice(0, region.start) + written + value.slice(end),
    region: { start: region.start, text: written },
    shift: written.length - region.text.length,
  };
};

/**
 * Move a region that sits at or after an edit made at `at`.
 *
 * Cleanup lands out of band: by the time one utterance's rewrite arrives, later
 * utterances have their own spans recorded against the old offsets. Rewriting
 * an earlier span moves every one of them.
 */
export const shiftRegion = (
  region: TranscriptRegion,
  at: number,
  shift: number,
): TranscriptRegion =>
  region.start >= at ? { ...region, start: region.start + shift } : region;
